import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  CalendarClock,
  CheckCircle2,
  Clock3,
  FileText,
  History,
  Paperclip,
  ShieldCheck,
} from "lucide-react";

import TitleButtons from "../../../../../components/TitleButtons";
import { useTheme } from "../../../../../context/ThemeContext";

type SectionKey =
  | "visao-geral"
  | "a-vencer"
  | "vencidas"
  | "anexados"
  | "historico";

type StatusValidade = "regular" | "a_vencer" | "vencido" | "sem_validade";

type Periodicidade =
  | "mensal"
  | "trimestral"
  | "semestral"
  | "anual"
  | "sem_validade"
  | "sob_demanda";

type TipoValidade =
  | "com_vencimento"
  | "sem_vencimento"
  | "sob_demanda"
  | "revisao_periodica";

type OperacionalKey =
  | "ok"
  | "pendente_anexo"
  | "vencida"
  | "em_renovacao"
  | "aguardando_validacao"
  | "pendente"
  | "nao_exigido";

type AnexoFilter = "vinculado" | "pendente";

type PortalResumo = {
  totalExigem: number;
  anexadosAtualizados: number;
  pendenteAnexo: number;
  vencidos: number;
  emRenovacao: number;
  aguardandoValidacao: number;
};

type OperacionalVisual = {
  key: OperacionalKey;
  label: string;
  background: string;
  color: string;
  border: string;
};

type CertidaoItem = {
  id: number;
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  empresa: string;
  cnpj: string;

  dataEmissao: string | null;
  dataValidade: string | null;

  tipoValidade: TipoValidade;
  periodicidade: Periodicidade;

  areaResponsavel: string;
  responsavelNome?: string | null;

  statusValidade: StatusValidade;

  anexoDisponivel: boolean;
  s3Key?: string | null;

  observacao?: string | null;

  versao: number;
  vigente: boolean;
  obsoleto: boolean;
  documentoSubstituidoId?: number | null;
  dataSubstituicao?: string | null;

  portais: PortalResumo;
};

type ColumnFilters = {
  documento: string;
  categoria: string;
  orgaoEmissor: string;
  validade: string;
  regra: string;
  responsavel: string;
  statusValidade: string;
  operacional: string;
  anexo: string;
};

type CertidoesDocumentosLayoutProps = {
  section?: SectionKey;
};

const BASE_PATH = "/governanca-qualidade-e-compliance/certidoes-e-documentos";

const DEPARTMENT_COLOR = "#2f3f59";

const sections: Array<{
  key: SectionKey;
  label: string;
  path: string;
  icon: React.ReactNode;
}> = [
  {
    key: "visao-geral",
    label: "Visão geral",
    path: `${BASE_PATH}/visao-geral`,
    icon: <ShieldCheck size={14} />,
  },
  {
    key: "a-vencer",
    label: "A vencer",
    path: `${BASE_PATH}/a-vencer`,
    icon: <CalendarClock size={14} />,
  },
  {
    key: "vencidas",
    label: "Vencidas",
    path: `${BASE_PATH}/vencidas`,
    icon: <AlertTriangle size={14} />,
  },
  {
    key: "anexados",
    label: "Anexados",
    path: `${BASE_PATH}/anexados`,
    icon: <Paperclip size={14} />,
  },
  {
    key: "historico",
    label: "Histórico",
    path: `${BASE_PATH}/historico`,
    icon: <History size={14} />,
  },
];

const initialFilters: ColumnFilters = {
  documento: "",
  categoria: "",
  orgaoEmissor: "",
  validade: "",
  regra: "",
  responsavel: "",
  statusValidade: "",
  operacional: "",
  anexo: "",
};

const mockCertidoes: CertidaoItem[] = [
  {
    id: 1,
    nome: "Certidão Negativa de Débitos Federais",
    categoria: "Fiscal",
    orgaoEmissor: "Receita Federal / PGFN",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "10/04/2026",
    dataValidade: "07/10/2026",
    tipoValidade: "com_vencimento",
    periodicidade: "semestral",
    areaResponsavel: "Financeiro",
    responsavelNome: "Contabilidade / Financeiro",
    statusValidade: "regular",
    anexoDisponivel: true,
    s3Key: "governanca/certidoes/cnd-federal-2026.pdf",
    observacao: null,
    versao: 2,
    vigente: true,
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 5,
      anexadosAtualizados: 5,
      pendenteAnexo: 0,
      vencidos: 0,
      emRenovacao: 0,
      aguardandoValidacao: 0,
    },
  },
  {
    id: 2,
    nome: "Certificado de Regularidade do FGTS",
    categoria: "Trabalhista",
    orgaoEmissor: "Caixa Econômica Federal",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "25/04/2026",
    dataValidade: "24/05/2026",
    tipoValidade: "com_vencimento",
    periodicidade: "mensal",
    areaResponsavel: "Departamento Pessoal",
    responsavelNome: "DP / Contabilidade",
    statusValidade: "a_vencer",
    anexoDisponivel: true,
    s3Key: "governanca/certidoes/crf-fgts-2026-04.pdf",
    observacao: "Renovação mensal deve ser acompanhada preventivamente.",
    versao: 1,
    vigente: true,
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 4,
      anexadosAtualizados: 1,
      pendenteAnexo: 0,
      vencidos: 0,
      emRenovacao: 3,
      aguardandoValidacao: 0,
    },
  },
  {
    id: 3,
    nome: "Certidão Municipal de Niterói",
    categoria: "Fiscal municipal",
    orgaoEmissor: "Prefeitura de Niterói",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: null,
    dataValidade: null,
    tipoValidade: "com_vencimento",
    periodicidade: "sob_demanda",
    areaResponsavel: "Financeiro",
    responsavelNome: "Financeiro",
    statusValidade: "sem_validade",
    anexoDisponivel: false,
    s3Key: null,
    observacao: "Documento ainda não vinculado ao repositório.",
    versao: 0,
    vigente: false,
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 3,
      anexadosAtualizados: 0,
      pendenteAnexo: 3,
      vencidos: 0,
      emRenovacao: 0,
      aguardandoValidacao: 0,
    },
  },
  {
    id: 4,
    nome: "Certidão Trabalhista / CNDT",
    categoria: "Trabalhista",
    orgaoEmissor: "TST",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "02/01/2026",
    dataValidade: "01/07/2026",
    tipoValidade: "com_vencimento",
    periodicidade: "semestral",
    areaResponsavel: "Departamento Pessoal",
    responsavelNome: "DP / Governança",
    statusValidade: "regular",
    anexoDisponivel: true,
    s3Key: "governanca/certidoes/cndt-2026.pdf",
    observacao: "Aguardando validação interna do arquivo vigente.",
    versao: 1,
    vigente: true,
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 4,
      anexadosAtualizados: 2,
      pendenteAnexo: 0,
      vencidos: 0,
      emRenovacao: 0,
      aguardandoValidacao: 2,
    },
  },
  {
    id: 5,
    nome: "Certidão Estadual",
    categoria: "Fiscal estadual",
    orgaoEmissor: "SEFAZ",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "01/12/2025",
    dataValidade: "01/03/2026",
    tipoValidade: "com_vencimento",
    periodicidade: "sob_demanda",
    areaResponsavel: "Financeiro",
    responsavelNome: "Financeiro / Contabilidade",
    statusValidade: "vencido",
    anexoDisponivel: true,
    s3Key: "governanca/certidoes/certidao-estadual-2025.pdf",
    observacao: "Documento vencido. Aguardando nova emissão.",
    versao: 1,
    vigente: false,
    obsoleto: true,
    documentoSubstituidoId: null,
    dataSubstituicao: null,
    portais: {
      totalExigem: 3,
      anexadosAtualizados: 1,
      pendenteAnexo: 0,
      vencidos: 2,
      emRenovacao: 0,
      aguardandoValidacao: 0,
    },
  },
  {
    id: 6,
    nome: "Contrato Social",
    categoria: "Societário",
    orgaoEmissor: "Junta Comercial",
    empresa: "HAVOX Desenvolvimento Tecnológico Ltda",
    cnpj: "57.588.843/0001-78",
    dataEmissao: "01/03/2024",
    dataValidade: null,
    tipoValidade: "sem_vencimento",
    periodicidade: "sem_validade",
    areaResponsavel: "Diretoria / Administrativo",
    responsavelNome: "Diretoria",
    statusValidade: "sem_validade",
    anexoDisponivel: true,
    s3Key: "governanca/documentos/contrato-social.pdf",
    observacao:
      "Documento permanente. Revisar apenas em caso de alteração contratual.",
    versao: 3,
    vigente: true,
    obsoleto: false,
    documentoSubstituidoId: 5,
    dataSubstituicao: "01/03/2024",
    portais: {
      totalExigem: 6,
      anexadosAtualizados: 6,
      pendenteAnexo: 0,
      vencidos: 0,
      emRenovacao: 0,
      aguardandoValidacao: 0,
    },
  },
];

function normalizeText(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function parseBrazilianDate(value: string | null): Date | null {
  if (!value) return null;

  const parts = value.split("/").map((part) => Number(part));

  if (parts.length !== 3) return null;

  const [day, month, year] = parts;

  if (!day || !month || !year) return null;

  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDaysDiffFromToday(date: Date | null) {
  if (!date) return null;

  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffMs = target.getTime() - today.getTime();

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function getPrazoOperacional(item: CertidaoItem) {
  if (item.tipoValidade === "sem_vencimento") {
    return "Sem vencimento";
  }

  if (item.tipoValidade === "sob_demanda") {
    return "Sob demanda";
  }

  if (item.tipoValidade === "revisao_periodica" && !item.dataValidade) {
    return "Revisão periódica";
  }

  const validade = parseBrazilianDate(item.dataValidade);
  const dias = getDaysDiffFromToday(validade);

  if (dias === null) {
    return "Validade não informada";
  }

  if (dias > 1) return `Faltam ${dias} dias`;
  if (dias === 1) return "Falta 1 dia";
  if (dias === 0) return "Vence hoje";
  if (dias === -1) return "Vencida há 1 dia";

  return `Vencida há ${Math.abs(dias)} dias`;
}

function pluralPortal(qtd: number) {
  return qtd === 1 ? "portal" : "portais";
}

function getOperacionalVisual(portais: PortalResumo): OperacionalVisual {
  if (!portais.totalExigem || portais.totalExigem <= 0) {
    return {
      key: "nao_exigido",
      label: "Não exigido em portais",
      background: "#f8fafc",
      color: "#475569",
      border: "#cbd5e1",
    };
  }

  if (portais.vencidos > 0) {
    return {
      key: "vencida",
      label: `Vencida em ${portais.vencidos} ${pluralPortal(portais.vencidos)}`,
      background: "#fef2f2",
      color: "#991b1b",
      border: "#fecaca",
    };
  }

  if (portais.pendenteAnexo > 0) {
    return {
      key: "pendente_anexo",
      label: `Pendente anexo em ${portais.pendenteAnexo} ${pluralPortal(
        portais.pendenteAnexo
      )}`,
      background: "#fff7ed",
      color: "#9a3412",
      border: "#fed7aa",
    };
  }

  if (portais.emRenovacao > 0) {
    return {
      key: "em_renovacao",
      label: `Renovação pendente em ${portais.emRenovacao} ${pluralPortal(
        portais.emRenovacao
      )}`,
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "#bfdbfe",
    };
  }

  if (portais.aguardandoValidacao > 0) {
    return {
      key: "aguardando_validacao",
      label: `Aguardando validação em ${
        portais.aguardandoValidacao
      } ${pluralPortal(portais.aguardandoValidacao)}`,
      background: "#f5f3ff",
      color: "#6d28d9",
      border: "#ddd6fe",
    };
  }

  if (portais.anexadosAtualizados >= portais.totalExigem) {
    return {
      key: "ok",
      label: `Atualizado em ${portais.totalExigem} ${pluralPortal(
        portais.totalExigem
      )}`,
      background: "#ecfdf3",
      color: "#166534",
      border: "#bbf7d0",
    };
  }

  const pendentesCalculados = portais.totalExigem - portais.anexadosAtualizados;

  return {
    key: "pendente",
    label: `Pendente em ${pendentesCalculados} ${pluralPortal(
      pendentesCalculados
    )}`,
    background: "#fffbeb",
    color: "#92400e",
    border: "#fde68a",
  };
}

function hasPendenciaOperacional(item: CertidaoItem) {
  const p = item.portais;

  return (
    p.vencidos > 0 ||
    p.pendenteAnexo > 0 ||
    p.emRenovacao > 0 ||
    p.aguardandoValidacao > 0 ||
    p.anexadosAtualizados < p.totalExigem ||
    !item.anexoDisponivel
  );
}

function getSectionFromPath(pathname: string): SectionKey {
  const path = pathname.toLowerCase();

  if (path.includes("a-vencer")) return "a-vencer";
  if (path.includes("vencidas")) return "vencidas";
  if (path.includes("anexados")) return "anexados";
  if (path.includes("historico") || path.includes("evidencias")) {
    return "historico";
  }

  return "visao-geral";
}

function getStatusValidadeLabel(status: StatusValidade) {
  switch (status) {
    case "regular":
      return "Regular";
    case "a_vencer":
      return "A vencer";
    case "vencido":
      return "Vencida";
    case "sem_validade":
      return "Sem validade";
    default:
      return "Indefinido";
  }
}

function getPeriodicidadeLabel(periodicidade: Periodicidade) {
  switch (periodicidade) {
    case "mensal":
      return "Mensal";
    case "trimestral":
      return "Trimestral";
    case "semestral":
      return "Semestral";
    case "anual":
      return "Anual";
    case "sem_validade":
      return "Sem validade";
    case "sob_demanda":
      return "Sob demanda";
    default:
      return "Indefinida";
  }
}

function getTipoValidadeLabel(tipo: TipoValidade) {
  switch (tipo) {
    case "com_vencimento":
      return "Com vencimento";
    case "sem_vencimento":
      return "Sem vencimento";
    case "sob_demanda":
      return "Sob demanda";
    case "revisao_periodica":
      return "Revisão periódica";
    default:
      return "Indefinido";
  }
}

function getStatusValidadeVisual(status: StatusValidade) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };
    case "a_vencer":
      return {
        background: "#fffbeb",
        color: "#92400e",
        border: "#fde68a",
        icon: <Clock3 size={12} />,
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };
    case "sem_validade":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <FileText size={12} />,
      };
  }
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "a-vencer":
      return "Certidões a vencer";
    case "vencidas":
      return "Certidões vencidas";
    case "anexados":
      return "Documentos anexados";
    case "historico":
      return "Histórico e evidências";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

function getUniqueOptions(
  items: CertidaoItem[],
  getter: (item: CertidaoItem) => string
) {
  return Array.from(
    new Set(
      items
        .map(getter)
        .map((value) => String(value ?? "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function getOperacionalFilterLabel(key: OperacionalKey) {
  switch (key) {
    case "ok":
      return "Atualizado";
    case "pendente_anexo":
      return "Pendente anexo";
    case "vencida":
      return "Vencida";
    case "em_renovacao":
      return "Renovação pendente";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "pendente":
      return "Pendente";
    case "nao_exigido":
      return "Não exigido";
    default:
      return "Indefinido";
  }
}

const FilterInput: React.FC<{
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}> = ({ value, placeholder, onChange }) => {
  return (
    <input
      value={value}
      placeholder={placeholder || "Filtrar"}
      onChange={(event) => onChange(event.target.value)}
      style={styles.filterInput}
    />
  );
};

const FilterSelect: React.FC<{
  value: string;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}> = ({ value, placeholder, options, onChange }) => {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={styles.filterSelect}
    >
      <option value="">{placeholder || "Todos"}</option>

      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const CertidoesDocumentosLayout: React.FC<CertidoesDocumentosLayoutProps> = ({
  section,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { departmentColor } = useTheme();
  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;

  const [filters, setFilters] = useState<ColumnFilters>(initialFilters);

  const activeSection = section || getSectionFromPath(location.pathname);

  const updateFilter = (key: keyof ColumnFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const filteredBySection = useMemo(() => {
    if (activeSection === "a-vencer") {
      return mockCertidoes.filter((item) => item.statusValidade === "a_vencer");
    }

    if (activeSection === "vencidas") {
      return mockCertidoes.filter((item) => item.statusValidade === "vencido");
    }

    if (activeSection === "anexados") {
      return mockCertidoes.filter((item) => item.anexoDisponivel);
    }

    if (activeSection === "historico") {
      return mockCertidoes.filter(
        (item) =>
          item.obsoleto ||
          item.versao > 1 ||
          hasPendenciaOperacional(item) ||
          item.anexoDisponivel
      );
    }

    return mockCertidoes;
  }, [activeSection]);

  const filterOptions = useMemo(() => {
    const categorias = getUniqueOptions(
      filteredBySection,
      (item) => item.categoria
    );

    const orgaos = getUniqueOptions(
      filteredBySection,
      (item) => item.orgaoEmissor
    );

    const responsaveis = getUniqueOptions(
      filteredBySection,
      (item) => item.areaResponsavel
    );

    const regras = Array.from(
      new Map(
        filteredBySection.map((item) => [
          item.tipoValidade,
          getTipoValidadeLabel(item.tipoValidade),
        ])
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    const statusValidade = Array.from(
      new Map(
        filteredBySection.map((item) => [
          item.statusValidade,
          getStatusValidadeLabel(item.statusValidade),
        ])
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    const operacional = Array.from(
      new Map(
        filteredBySection.map((item) => {
          const visual = getOperacionalVisual(item.portais);
          return [visual.key, getOperacionalFilterLabel(visual.key)];
        })
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    return {
      categorias: categorias.map((value) => ({ value, label: value })),
      orgaos: orgaos.map((value) => ({ value, label: value })),
      regras,
      responsaveis: responsaveis.map((value) => ({ value, label: value })),
      statusValidade,
      operacional,
      anexos: [
        { value: "vinculado", label: "Vinculado" },
        { value: "pendente", label: "Pendente" },
      ],
    };
  }, [filteredBySection]);

  const filteredItems = useMemo(() => {
    return filteredBySection.filter((item) => {
      const operacional = getOperacionalVisual(item.portais);

      const documentoSearch = normalizeText(filters.documento);
      const validadeSearch = normalizeText(filters.validade);

      const documentoMatch =
        !documentoSearch ||
        [
          item.nome,
          item.cnpj,
          item.empresa,
          item.versao > 0 ? `v${item.versao}` : "",
          item.obsoleto ? "obsoleto" : "",
        ].some((field) => normalizeText(field).includes(documentoSearch));

      const categoriaMatch =
        !filters.categoria || item.categoria === filters.categoria;

      const orgaoMatch =
        !filters.orgaoEmissor || item.orgaoEmissor === filters.orgaoEmissor;

      const validadeMatch =
        !validadeSearch ||
        [
          item.dataValidade || "Não informada",
          getPrazoOperacional(item),
          item.dataEmissao
            ? `Emissão: ${item.dataEmissao}`
            : "Emissão: Não informada",
        ].some((field) => normalizeText(field).includes(validadeSearch));

      const regraMatch = !filters.regra || item.tipoValidade === filters.regra;

      const responsavelMatch =
        !filters.responsavel || item.areaResponsavel === filters.responsavel;

      const statusValidadeMatch =
        !filters.statusValidade ||
        item.statusValidade === filters.statusValidade;

      const operacionalMatch =
        !filters.operacional || operacional.key === filters.operacional;

      const anexoValue: AnexoFilter = item.anexoDisponivel
        ? "vinculado"
        : "pendente";

      const anexoMatch = !filters.anexo || anexoValue === filters.anexo;

      return (
        documentoMatch &&
        categoriaMatch &&
        orgaoMatch &&
        validadeMatch &&
        regraMatch &&
        responsavelMatch &&
        statusValidadeMatch &&
        operacionalMatch &&
        anexoMatch
      );
    });
  }, [filteredBySection, filters]);

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Certidões e Documentos - ${getSectionTitle(activeSection)}`}
      />

      <div style={styles.content}>
        <section style={styles.navigationBar}>
          <nav style={styles.tabs}>
            {sections.map((item) => {
              const active = item.key === activeSection;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.path)}
                  style={{
                    ...styles.tabButton,
                    ...(active ? styles.tabButtonActive : {}),
                    ...(active
                      ? {
                          background: toRgba(activeDepartmentColor, 0.08),
                          borderColor: activeDepartmentColor,
                          color: activeDepartmentColor,
                        }
                      : {}),
                  }}
                >
                  <span style={styles.tabIcon}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>
        </section>

        {(activeSection === "a-vencer" || activeSection === "vencidas") && (
          <section
            style={{
              ...styles.notice,
              ...(activeSection === "vencidas"
                ? styles.noticeDanger
                : styles.noticeWarning),
            }}
          >
            <div style={styles.noticeIcon}>
              {activeSection === "vencidas" ? (
                <AlertTriangle size={16} />
              ) : (
                <CalendarClock size={16} />
              )}
            </div>

            <div>
              <div style={styles.noticeTitle}>
                {activeSection === "vencidas"
                  ? "Regularização necessária"
                  : "Renovação preventiva recomendada"}
              </div>

              <div style={styles.noticeText}>
                {activeSection === "vencidas"
                  ? "Existem documentos vencidos. Recomenda-se tratar a regularização como prioridade operacional."
                  : "Documentos próximos ao vencimento devem ser acompanhados com antecedência para evitar perda de regularidade."}
              </div>
            </div>
          </section>
        )}

        {activeSection === "historico" ? (
          <HistoricoPanel departmentColor={activeDepartmentColor} />
        ) : (
          <section style={styles.tablePanel}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Documento</th>
                    <th style={styles.th}>Categoria</th>
                    <th style={styles.th}>Órgão emissor</th>
                    <th style={styles.th}>Validade</th>
                    <th style={styles.th}>Regra</th>
                    <th style={styles.th}>Responsável</th>
                    <th style={styles.th}>Status validade</th>
                    <th style={styles.th}>Operacional</th>
                    <th style={styles.th}>Anexo</th>
                    <th style={styles.th}>Pendência</th>
                  </tr>

                  <tr>
                    <th style={styles.filterTh}>
                      <FilterInput
                        value={filters.documento}
                        placeholder="Documento"
                        onChange={(value) => updateFilter("documento", value)}
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <FilterSelect
                        value={filters.categoria}
                        placeholder="Todos"
                        options={filterOptions.categorias}
                        onChange={(value) => updateFilter("categoria", value)}
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <FilterSelect
                        value={filters.orgaoEmissor}
                        placeholder="Todos"
                        options={filterOptions.orgaos}
                        onChange={(value) =>
                          updateFilter("orgaoEmissor", value)
                        }
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <FilterInput
                        value={filters.validade}
                        placeholder="Validade"
                        onChange={(value) => updateFilter("validade", value)}
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <FilterSelect
                        value={filters.regra}
                        placeholder="Todos"
                        options={filterOptions.regras}
                        onChange={(value) => updateFilter("regra", value)}
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <FilterSelect
                        value={filters.responsavel}
                        placeholder="Todos"
                        options={filterOptions.responsaveis}
                        onChange={(value) => updateFilter("responsavel", value)}
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <FilterSelect
                        value={filters.statusValidade}
                        placeholder="Todos"
                        options={filterOptions.statusValidade}
                        onChange={(value) =>
                          updateFilter("statusValidade", value)
                        }
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <FilterSelect
                        value={filters.operacional}
                        placeholder="Todos"
                        options={filterOptions.operacional}
                        onChange={(value) => updateFilter("operacional", value)}
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <FilterSelect
                        value={filters.anexo}
                        placeholder="Todos"
                        options={filterOptions.anexos}
                        onChange={(value) => updateFilter("anexo", value)}
                      />
                    </th>

                    <th style={styles.filterTh}>
                      <div style={styles.filterDisabled} />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredItems.map((item) => (
                    <DocumentRow
                      key={item.id}
                      item={item}
                      departmentColor={activeDepartmentColor}
                      onDoubleClick={() =>
                        navigate(`${BASE_PATH}/documento/${item.id}`)
                      }
                    />
                  ))}

                  {filteredItems.length === 0 && (
                    <tr>
                      <td colSpan={10} style={styles.emptyCell}>
                        Nenhum documento encontrado para esta visão.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

const DocumentRow: React.FC<{
  item: CertidaoItem;
  departmentColor: string;
  onDoubleClick?: () => void;
}> = ({ item, departmentColor, onDoubleClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const statusValidadeVisual = getStatusValidadeVisual(item.statusValidade);
  const operacional = getOperacionalVisual(item.portais);

  const rowHoverBackground = toRgba(departmentColor, 0.055);
  const rowHoverBorder = toRgba(departmentColor, 0.22);
  const rowHoverIconBackground = toRgba(departmentColor, 0.08);
  const rowHoverIconBorder = toRgba(departmentColor, 0.28);

  const tdStyle: React.CSSProperties = {
    ...styles.td,
    borderTopColor: isHovered ? rowHoverBorder : "#f0f2f4",
    borderBottomColor: isHovered ? rowHoverBorder : "#f0f2f4",
  };

  return (
    <tr
      style={{
        ...styles.tr,
        background: isHovered ? rowHoverBackground : "#ffffff",
        boxShadow: isHovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
        transition:
          "background 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={onDoubleClick}
      title="Clique duas vezes para abrir os detalhes do documento"
    >
      <td style={tdStyle}>
        <div style={styles.docCell}>
          <div
            style={{
              ...styles.docIcon,
              ...(isHovered
                ? {
                    background: rowHoverIconBackground,
                    borderColor: rowHoverIconBorder,
                    color: departmentColor,
                  }
                : {}),
            }}
          >
            <FileText size={14} />
          </div>

          <div style={styles.docInfo}>
            <div
              style={{
                ...styles.docName,
                color: isHovered ? departmentColor : "#111827",
              }}
            >
              {item.nome}
            </div>

            <div style={styles.docMeta}>
              {item.cnpj}
              {item.versao > 0 ? ` • v${item.versao}` : ""}
              {item.obsoleto ? " • Obsoleto" : ""}
            </div>
          </div>
        </div>
      </td>

      <td style={tdStyle}>{item.categoria}</td>

      <td style={tdStyle}>{item.orgaoEmissor}</td>

      <td style={tdStyle}>
        <div style={styles.dateText}>
          {item.dataValidade || "Não informada"}
        </div>

        <div
          style={{
            ...styles.prazoText,
            ...(item.statusValidade === "vencido" ? styles.prazoDanger : {}),
            ...(item.statusValidade === "a_vencer" ? styles.prazoWarning : {}),
          }}
        >
          {getPrazoOperacional(item)}
        </div>

        <div style={styles.docMeta}>
          Emissão: {item.dataEmissao || "Não informada"}
        </div>
      </td>

      <td style={tdStyle}>
        <div style={styles.ruleText}>
          {getTipoValidadeLabel(item.tipoValidade)}
        </div>

        <div style={styles.docMeta}>
          {getPeriodicidadeLabel(item.periodicidade)}
        </div>
      </td>

      <td style={tdStyle}>
        <div style={styles.responsavelArea}>{item.areaResponsavel}</div>

        {item.responsavelNome && (
          <div style={styles.docMeta}>{item.responsavelNome}</div>
        )}
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.statusBadge,
            background: statusValidadeVisual.background,
            color: statusValidadeVisual.color,
            borderColor: statusValidadeVisual.border,
          }}
        >
          {statusValidadeVisual.icon}
          {getStatusValidadeLabel(item.statusValidade)}
        </span>
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.statusBadge,
            background: operacional.background,
            color: operacional.color,
            borderColor: operacional.border,
          }}
        >
          {operacional.label}
        </span>
      </td>

      <td style={tdStyle}>
        {item.anexoDisponivel ? (
          <span style={styles.attachmentOk}>
            <Paperclip size={12} />
            Vinculado
          </span>
        ) : (
          <span style={styles.attachmentPending}>Pendente</span>
        )}
      </td>

      <td style={tdStyle}>
        {item.observacao ? (
          <span style={styles.observacaoText}>{item.observacao}</span>
        ) : (
          <span style={styles.docMeta}>Sem pendência</span>
        )}
      </td>
    </tr>
  );
};

const HistoricoPanel: React.FC<{ departmentColor: string }> = ({
  departmentColor,
}) => {
  const eventos = [
    {
      id: 1,
      data: "07/05/2026 16:42",
      evento: "Documento vinculado",
      detalhe:
        "Certidão Negativa de Débitos Federais anexada ao repositório corporativo.",
      responsavel: "Governança",
    },
    {
      id: 2,
      data: "07/05/2026 17:05",
      evento: "Renovação iniciada",
      detalhe: "Certificado de Regularidade do FGTS marcado como em renovação.",
      responsavel: "Departamento Pessoal",
    },
    {
      id: 3,
      data: "07/05/2026 17:20",
      evento: "Pendência identificada",
      detalhe: "Certidão Municipal ainda sem arquivo vinculado.",
      responsavel: "Financeiro",
    },
    {
      id: 4,
      data: "07/05/2026 17:40",
      evento: "Documento obsoleto",
      detalhe: "Certidão Estadual vencida mantida apenas para histórico.",
      responsavel: "Financeiro",
    },
  ];

  return (
    <section style={styles.historyPanel}>
      <div style={styles.tableHeader}>
        <div>
          <h2 style={styles.tableTitle}>Histórico documental</h2>
          <p style={styles.tableSubtitle}>
            Linha do tempo de eventos, versões, substituições e pendências.
          </p>
        </div>

        <div style={styles.tableCount}>
          {eventos.length} {eventos.length === 1 ? "evento" : "eventos"}
        </div>
      </div>

      <div style={styles.timeline}>
        {eventos.map((evento) => (
          <div key={evento.id} style={styles.timelineItem}>
            <div style={styles.timelineDate}>{evento.data}</div>

            <div style={styles.timelineBody}>
              <div style={styles.timelineTop}>
                <span style={styles.timelineTitle}>{evento.evento}</span>
                <span
                  style={{
                    ...styles.timelineUser,
                    color: departmentColor,
                  }}
                >
                  Responsável: {evento.responsavel}
                </span>
              </div>

              <div style={styles.timelineText}>{evento.detalhe}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  navigationBar: {
    minHeight: 42,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "5px 8px",
  },

  tabs: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    overflowX: "auto",
    minWidth: 0,
  },

  tabButton: {
    height: 30,
    padding: "0 10px",
    border: "1px solid transparent",
    background: "transparent",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  tabButtonActive: {
    background: "rgba(47, 63, 88, 0.08)",
    borderColor: DEPARTMENT_COLOR,
    color: DEPARTMENT_COLOR,
  },

  tabIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "inherit",
  },

  notice: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid",
  },

  noticeWarning: {
    background: "#fff8e8",
    borderColor: "#e7cf9a",
    color: "#6b4f1f",
  },

  noticeDanger: {
    background: "#fff1f1",
    borderColor: "#efb4b4",
    color: "#7f1d1d",
  },

  noticeIcon: {
    marginTop: 1,
    display: "inline-flex",
    flexShrink: 0,
  },

  noticeTitle: {
    fontSize: 13,
    fontWeight: 850,
    marginBottom: 2,
  },

  noticeText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.35,
  },

  tablePanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  tableHeader: {
    minHeight: 48,
    padding: "10px 12px",
    borderBottom: "1px solid #dfe3e8",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  tableTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 900,
    color: "#111827",
    lineHeight: 1.25,
  },

  tableSubtitle: {
    margin: "2px 0 0",
    fontSize: 11.5,
    fontWeight: 600,
    color: "#64748b",
    lineHeight: 1.25,
  },

  tableCount: {
    fontSize: 11.5,
    fontWeight: 800,
    color: "#64748b",
    whiteSpace: "nowrap",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1320,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    width: "10%",
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 900,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  filterTh: {
    width: "10%",
    padding: "5px 6px",
    background: "#ffffff",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
  },

  filterInput: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 7px",
    fontSize: 11.5,
    fontWeight: 650,
    outline: "none",
    fontFamily: "inherit",
  },

  filterSelect: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 6px",
    fontSize: 11.5,
    fontWeight: 650,
    outline: "none",
    fontFamily: "inherit",
  },

  filterDisabled: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
  },

  tr: {
    borderBottom: "1px solid #edf0f2",
    cursor: "pointer",
    background: "#ffffff",
  },

  td: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  docCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  docIcon: {
    width: 28,
    height: 28,
    background: "#f2f5f8",
    border: "1px solid #e2e8f0",
    color: DEPARTMENT_COLOR,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  docInfo: {
    minWidth: 0,
  },

  docName: {
    fontSize: 12.4,
    fontWeight: 850,
    color: "#111827",
    lineHeight: 1.25,
  },

  docMeta: {
    marginTop: 1,
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
  },

  dateText: {
    fontSize: 12.2,
    fontWeight: 800,
    color: "#111827",
  },

  prazoText: {
    marginTop: 1,
    fontSize: 11.4,
    fontWeight: 800,
    color: "#166534",
  },

  prazoWarning: {
    color: "#92400e",
  },

  prazoDanger: {
    color: "#991b1b",
  },

  ruleText: {
    fontSize: 12.2,
    fontWeight: 800,
    color: "#334155",
  },

  responsavelArea: {
    fontSize: 12.2,
    fontWeight: 850,
    color: "#334155",
  },

  statusBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  attachmentOk: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "#166534",
    fontSize: 11.8,
    fontWeight: 850,
  },

  attachmentPending: {
    color: "#92400e",
    fontSize: 11.8,
    fontWeight: 850,
  },

  observacaoText: {
    display: "inline-block",
    maxWidth: 230,
    color: "#6b4f1f",
    fontSize: 11.5,
    fontWeight: 700,
    lineHeight: 1.3,
  },

  emptyCell: {
    padding: 26,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },

  historyPanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  timeline: {
    padding: "10px 12px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  timelineItem: {
    display: "grid",
    gridTemplateColumns: "150px minmax(0, 1fr)",
    gap: 10,
    alignItems: "stretch",
  },

  timelineDate: {
    padding: "10px",
    background: "#f4f6f8",
    border: "1px solid #dfe3e8",
    fontSize: 11.5,
    fontWeight: 800,
    color: "#64748b",
    display: "flex",
    alignItems: "center",
  },

  timelineBody: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: "10px 12px",
  },

  timelineTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 4,
  },

  timelineTitle: {
    fontSize: 13,
    fontWeight: 900,
    color: "#111827",
  },

  timelineText: {
    fontSize: 12.3,
    fontWeight: 600,
    color: "#475569",
    lineHeight: 1.4,
  },

  timelineUser: {
    fontSize: 11.5,
    fontWeight: 800,
    color: DEPARTMENT_COLOR,
    whiteSpace: "nowrap",
  },
};

export default CertidoesDocumentosLayout;
